import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef
} from "react";
import TableContext, { TableContextValues } from "../task/TableContext";
import {
  useSocketEvent,
  WhatsAppNotificationEvent
} from "../shared/SocketContext";
import CounterMsgContext from "../shared/CounterMsgContext";
import ActivityService from "../../services/ActivityService";
import { SortDirection } from "../shared/entities/Sort";
import {
  applyFiltersHelper,
  calculatePageCount,
  setCurrentPageHelper,
  sortHelper,
  useEndpointData
} from "../../utils/TableUtils";
import { ActivityListResponse } from "../client/activity/entities/AcitivityListResponse";

export enum QuickFilterType {
  Whatsapp = 6,
  WhatsappInbound = 7,
  Email = 8
}

export class TypeListRequest {
  constructor(quickfilter: number) {
    this.quickfilter = quickfilter;
  }
  page: number = 0;
  sortColumn: string = "personName";
  descending: SortDirection = SortDirection.Descending;
  text: string | null = null;
  impid: null = null;
  actionsentid: null = null;
  reason: null = null;
  pagesize: number = 15;
  filter: string | null = null;
  quickfilter: number = QuickFilterType.Email;
  archived?: boolean = false;
  read?: boolean = false;
  spam?: boolean = false;
  inbound?: boolean;
  _v?: number;
}

class InboxTableContextValues extends TableContextValues<ActivityListResponse> {
  setViewRead: (val: boolean | undefined) => void = () => {};
  setViewArchived: (val: boolean) => void = () => {};
  viewArchived: boolean = false;
  request: TypeListRequest = new TypeListRequest(QuickFilterType.Whatsapp);
  setRequest: React.Dispatch<React.SetStateAction<TypeListRequest>> = () => {};
  viewRead?: boolean = false;
  /** Nuevo: forma explícita de volver a cargar los datos sin cambiar filtros */
  refetch: () => void = () => {};
}

export const InboxContext = createContext(new InboxTableContextValues());

const InboxTableProvider = ({
  children,
  prevRequest,
  enabled = true
}: React.PropsWithChildren<{
  prevRequest?: TypeListRequest;
  enabled?: boolean;
}>) => {
  const [request, setRequest] = useState(
    prevRequest ?? new TypeListRequest(QuickFilterType.Email)
  );
  const prevQuickfilterRef = useRef<number>(request.quickfilter);

  // Resetear filtros cuando cambia el quickfilter (tab)
  useEffect(() => {
    if (prevQuickfilterRef.current !== request.quickfilter) {
      const newQuickfilter = request.quickfilter;
      prevQuickfilterRef.current = newQuickfilter;

      // Aplicar filtros por defecto según el quickfilter
      // Preservamos archived si está en true (carpeta Archivados) o spam si está en true (carpeta Spam)
      const currentArchived = request.archived === true;
      const currentSpam = request.spam === true;

      const defaultFilters =
        newQuickfilter === QuickFilterType.Email
          ? {
              // Para Email: spam false (excepto si está en carpeta Spam), read undefined, archived undefined (excepto si está en Archivados)
              spam: currentSpam ? true : false,
              read: undefined,
              archived: currentArchived ? true : undefined
            }
          : {
              // Para WhatsApp: spam false, read false, archived undefined (excepto si está en Archivados)
              spam: false,
              read: false,
              archived: currentArchived ? true : undefined
            };

      // Resetear filtros, búsqueda y aplicar filtros por defecto, forzar refresh
      setRequest((prev) => ({
        ...prev,
        filter: null,
        text: null,
        page: 0,
        ...defaultFilters,
        _v: Date.now() // Forzar refresh de la tabla
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.quickfilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const endpoint = useCallback(() => {
    let requestToSend = { ...request, inbound: true };

    if (request.quickfilter === QuickFilterType.Email) {
      if (request.spam !== true) {
        requestToSend.spam = false;
      }
      if (request.archived !== true) {
        requestToSend.archived = false;
      }
    } else {
      if (request.archived !== true) {
        requestToSend.archived = false;
      }
    }

    return ActivityService.getInboxList(requestToSend);
    // JSON.stringify como dep hace comparación profunda: evita llamadas extra
    // cuando setRequest crea una nueva referencia pero el contenido no cambió
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(request)]);

  // Hook que ejecuta el endpoint y expone reload
  const { loading, error, response, reload } = useEndpointData(
    endpoint,
    enabled
  );

  const pageCount = calculatePageCount(response);
  const { sort, setSortColumn } = sortHelper(request, setRequest);
  const { getCounterInbox } = useContext(CounterMsgContext);

  const refetch = useCallback(() => {
    setRequest((prev) => ({ ...prev, _v: Date.now() })); // fuerza nueva identidad de endpoint
  }, []);

  useSocketEvent(WhatsAppNotificationEvent.CallReceived, (payload: any) => {
    const isWhatsApp =
      payload?.Type === 6 ||
      payload?.type === 6 ||
      request.quickfilter === QuickFilterType.Whatsapp ||
      request.quickfilter === QuickFilterType.WhatsappInbound;

    if (isWhatsApp || request.quickfilter === QuickFilterType.Email) {
      getCounterInbox?.();
      if (enabled) refetch();
    }
  });
  useSocketEvent(WhatsAppNotificationEvent.CallTaken, () => {
    if (
      request.quickfilter === QuickFilterType.Whatsapp ||
      request.quickfilter === QuickFilterType.WhatsappInbound
    ) {
      getCounterInbox?.();
      if (enabled) refetch();
    }
  });

  const applySearch = (keyword: any) => {
    setRequest((prev) => ({ ...prev, text: keyword }));
  };

  const [viewRead, setViewRead] = useState<boolean | undefined>();
  const [viewArchived, setViewArchived] = useState<boolean>(false);

  const value: InboxTableContextValues = {
    loading,
    error,
    response,
    setSortColumn,
    sort,
    currentPage: request.page,
    setCurrentPage: setCurrentPageHelper(setRequest, pageCount),
    pageCount,
    applyFilters: applyFiltersHelper(setRequest),
    applySearch,
    reload,
    refetch,
    setViewRead,
    setViewArchived,
    viewArchived,
    viewRead,
    request,
    setRequest,
    pageSize: request.pagesize
  };

  return (
    <InboxContext.Provider value={value}>
      <TableContext.Provider value={value}>{children}</TableContext.Provider>
    </InboxContext.Provider>
  );
};

export default InboxTableProvider;
